import { Injectable, computed, signal } from '@angular/core';
import {
  DomainAnalyticsDto,
  DomainBrandingDto,
  DomainCanonicalMode,
  DomainDefaultLang,
  DomainLegalEntityDto,
  DomainMode,
  DomainPrimaryCtaDto,
  DomainResolveDto,
  DomainSocialLinksDto,
  PortfolioObjectDto,
} from '../api/models';

/**
 * App-level branding tokens we override at runtime when in white-label mode.
 * A single primary colour is mapped onto the PrimeNG + Tailwind brand tokens
 * the UI actually references, so buttons/accents pick up the owner's colour.
 */
const PRIMARY_VARS = [
  '--p-primary-color',
  '--p-primary-400',
  '--p-primary-500',
  '--p-primary-600',
  '--p-primary-700',
  '--color-brand-500',
  '--color-brand-600',
  '--orange-500',
  '--orange-600',
];

/**
 * Accent-oriented tokens. Mapping the owner's accent colour onto a small set of
 * secondary / tag tokens makes the accent actually visible in the UI (chips,
 * tags, secondary surfaces) instead of only living in `--brand-accent`.
 */
const ACCENT_VARS = [
  '--brand-accent',
  '--p-secondary-color',
  '--p-tag-primary-background',
  '--p-chip-background',
];

/**
 * Holds the resolved white-label state and applies it to the document.
 * Populated once at startup by the white-label APP_INITIALIZER, only on
 * branded hosts (resolve === 200). On the main host it stays inert.
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly _branding = signal<DomainBrandingDto | null>(null);
  private readonly _slug = signal<string | null>(null);
  private readonly _isWhiteLabel = signal(false);
  private readonly _mode = signal<DomainMode>('single');
  private readonly _portfolioObjects = signal<PortfolioObjectDto[]>([]);
  private readonly _brandingCreditRemovable = signal(false);

  readonly branding = this._branding.asReadonly();
  readonly slug = this._slug.asReadonly();
  readonly isWhiteLabel = this._isWhiteLabel.asReadonly();

  readonly displayName = computed(() => this._branding()?.displayName ?? null);
  readonly logoUrl = computed(() => this._branding()?.logoUrl ?? null);
  readonly primaryColor = computed(() => this._branding()?.primaryColor ?? null);
  readonly accentColor = computed(() => this._branding()?.accentColor ?? null);

  /** Normalised (#rrggbb) colour values, safe to drop straight into CSS. */
  readonly accentColorCss = computed(() => normalizeColor(this._branding()?.accentColor));
  readonly primaryColorCss = computed(() => normalizeColor(this._branding()?.primaryColor));

  // ── White-label-aware action policy ──────────────────────────────────────
  // On the main (unbranded) app everything is shown. On a white-label host the
  // owner opts features in/out; the defaults below apply when left undefined.

  readonly showShare = computed(() => {
    const b = this._branding();
    return !this._isWhiteLabel() || (b?.showShare ?? true);
  });

  readonly showFavorites = computed(() => {
    const b = this._branding();
    return !this._isWhiteLabel() || (b?.showFavorites ?? false);
  });

  readonly showReport = computed(() => {
    const b = this._branding();
    return !this._isWhiteLabel() || (b?.showReport ?? false);
  });

  // Chat is shown by default everywhere; the component combines this with the
  // thread count to decide final visibility.
  readonly showChat = computed(() => this._branding()?.showChat ?? true);

  readonly showCopyright = computed(() => this._branding()?.showCopyright ?? true);
  readonly copyrightText = computed(
    () => this._branding()?.copyrightText ?? this.displayName(),
  );
  // The backend forces this true when the org can't remove branding, so the
  // server-corrected value is taken as-is (default true when undefined).
  readonly showPoweredBy = computed(() => this._branding()?.showPoweredBy ?? true);

  // ── Mode & portfolio ─────────────────────────────────────────────────────

  /** How the host renders: a single object page or a portfolio listing. */
  readonly mode = computed<DomainMode>(() => this._mode());
  readonly isPortfolio = computed(() => this._mode() === 'portfolio');
  readonly portfolioObjects = computed<PortfolioObjectDto[]>(
    () => this._portfolioObjects(),
  );

  /** Whether the org's plan lets them remove the "powered by" branding credit. */
  readonly brandingCreditRemovable = computed(() => this._brandingCreditRemovable());

  // ── Plain value passthroughs ─────────────────────────────────────────────

  readonly socialLinks = computed<DomainSocialLinksDto | null>(
    () => this._branding()?.socialLinks ?? null,
  );
  readonly faviconUrl = computed(() => this._branding()?.faviconUrl ?? null);
  readonly ogImageUrl = computed(() => this._branding()?.ogImageUrl ?? null);
  readonly heroImageUrl = computed(() => this._branding()?.heroImageUrl ?? null);
  readonly heroTagline = computed(() => this._branding()?.heroTagline ?? null);
  readonly canonicalMode = computed<DomainCanonicalMode>(
    () => this._branding()?.canonicalMode ?? 'zaklepuj',
  );
  readonly metaTitle = computed(() => this._branding()?.metaTitle ?? null);
  readonly metaDescription = computed(() => this._branding()?.metaDescription ?? null);
  readonly defaultLang = computed<DomainDefaultLang | null>(
    () => this._branding()?.defaultLang ?? null,
  );
  readonly primaryCta = computed<DomainPrimaryCtaDto | null>(
    () => this._branding()?.primaryCta ?? null,
  );
  readonly analytics = computed<DomainAnalyticsDto | null>(
    () => this._branding()?.analytics ?? null,
  );

  /**
   * Whether the host configured any COOKIE-based analytics tag (GA4 / Meta
   * Pixel / GTM). These store/read device data and therefore require a RODO
   * consent banner. Cookieless CapyStats deliberately does NOT count here — a
   * host running only CapyStats needs no banner.
   */
  readonly hasCookieAnalytics = computed(() => {
    const a = this.analytics();
    return !!(
      a?.ga4MeasurementId?.trim() ||
      a?.metaPixelId?.trim() ||
      a?.gtmContainerId?.trim()
    );
  });
  readonly legalEntity = computed<DomainLegalEntityDto | null>(
    () => this._branding()?.legalEntity ?? null,
  );

  /** Whether search engines may index this host (defaults to true). */
  readonly seoIndexable = computed(() => this._branding()?.seoIndexable ?? true);

  readonly newsletterEnabled = computed(() => this._branding()?.newsletterEnabled ?? false);
  readonly newsletterHeadline = computed<string | null>(
    () => this._branding()?.newsletterHeadline ?? null,
  );

  /** Activate white-label mode from a successful resolve response. */
  apply(resolve: DomainResolveDto): void {
    this._isWhiteLabel.set(true);
    this._slug.set(resolve.slug ?? null);
    this._branding.set(resolve.branding ?? null);
    this._mode.set(resolve.mode ?? 'single');
    this._portfolioObjects.set(resolve.objects ?? []);
    this._brandingCreditRemovable.set(resolve.brandingCreditRemovable ?? false);
    this.applyCssVariables(resolve.branding);
    this.applyFavicon(resolve.branding?.faviconUrl);
  }

  private applyCssVariables(branding: DomainBrandingDto | null): void {
    if (!branding || typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    const primary = normalizeColor(branding.primaryColor);
    const accent = normalizeColor(branding.accentColor);

    if (primary) {
      root.style.setProperty('--brand-primary', primary);
      for (const cssVar of PRIMARY_VARS) {
        root.style.setProperty(cssVar, primary);
      }
    }
    if (accent) {
      for (const cssVar of ACCENT_VARS) {
        root.style.setProperty(cssVar, accent);
      }
    }
  }

  /**
   * Create-or-replace the favicon (and apple-touch-icon) in <head>. Only runs
   * when a faviconUrl is supplied and a document is available (guards SSR).
   */
  private applyFavicon(faviconUrl?: string | null): void {
    if (!faviconUrl || typeof document === 'undefined') {
      return;
    }
    const head = document.head;
    if (!head) {
      return;
    }
    this.setIconLink(head, 'icon', faviconUrl);
    this.setIconLink(head, 'apple-touch-icon', faviconUrl);
  }

  private setIconLink(head: HTMLHeadElement, rel: string, href: string): void {
    let link = head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', rel);
      head.appendChild(link);
    }
    link.setAttribute('href', href);
  }
}

/**
 * Accept a 6-hex colour (with or without leading #) and return it normalised
 * as `#rrggbb`, or null when invalid/empty.
 */
function normalizeColor(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  const v = value.trim();
  if (!/^#?[0-9a-fA-F]{6}$/.test(v)) {
    return null;
  }
  return v.startsWith('#') ? v : `#${v}`;
}
